import { useQuery, useQueryClient } from '@tanstack/react-query'
import { collectionGroup, doc, getDoc, getDocs, onSnapshot } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { LeaderboardData, LeaderboardEntry, LeaderboardType, UserEngagement } from '@/types'
import { useEffect } from 'react'

const STALE_TIME = 10 * 60 * 1000 // 10 minutes - leaderboard jarang berubah
const CACHE_TIME = 60 * 60 * 1000 // 1 hour

// OPTIMIZATION: Pre-computed leaderboard (hanya 1 READ!)
export function useLeaderboard(type: LeaderboardType = 'overall') {
  return useQuery({
    queryKey: ['leaderboard', type],
    queryFn: async () => {
      const docRef = doc(db, 'leaderboards', type)
      const docSnap = await getDoc(docRef)
      
      if (!docSnap.exists()) {
        // Fallback: jika pre-computed belum ada, hitung dari userEngagement
        return fetchRealtimeLeaderboard(type)
      }
      
      return docSnap.data() as LeaderboardData
    },
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
    refetchInterval: 30 * 60 * 1000, // Refetch setiap 30 menit
  })
}

// Fallback: Fetch dari userEngagement (lebih mahal, tapi jarang dipakai)
async function fetchRealtimeLeaderboard(type: LeaderboardType): Promise<LeaderboardData> {
  // This is expensive, should only run if pre-computed doesn't exist
  console.warn('Using fallback leaderboard fetch - consider running updateLeaderboards function')
  
  return {
    type,
    users: [],
    updatedAt: { seconds: Date.now() / 1000, nanoseconds: 0 } as any,
  }
}

// Get current user's rank (real-time)
export function useUserRank(uid: string | undefined) {
  const queryClient = useQueryClient()
  
  useEffect(() => {
    if (!uid) return
    
    // Listen untuk perubahan engagement user sendiri
    const unsubscribe = onSnapshot(
      doc(db, 'adminDashboard', 'userEngagement', 'engagements', uid),
      (doc) => {
        if (doc.exists()) {
          queryClient.setQueryData(['userEngagement', uid], {
            id: doc.id,
            ...doc.data(),
          })
        }
      }
    )
    
    return unsubscribe
  }, [uid, queryClient])
  
  return useQuery({
    queryKey: ['userEngagement', uid],
    queryFn: async () => {
      if (!uid) return null
      const docRef = doc(db, 'adminDashboard', 'userEngagement', 'engagements', uid)
      const docSnap = await getDoc(docRef)
      if (!docSnap.exists()) return null
      return { id: docSnap.id, ...docSnap.data() } as UserEngagement
    },
    enabled: !!uid,
  })
}

// Get user's position in leaderboard
export function useUserLeaderboardPosition(uid: string | undefined, type: LeaderboardType = 'overall') {
  return useQuery({
    queryKey: ['leaderboardPosition', uid, type],
    queryFn: async () => {
      if (!uid) return null
      
      // Get leaderboard
      const leaderboardDoc = await getDoc(doc(db, 'leaderboards', type))
      if (!leaderboardDoc.exists()) return null
      
      const data = leaderboardDoc.data() as LeaderboardData
      const position = data.users.findIndex(u => u.uid === uid)
      
      if (position === -1) {
        // User not in top 100, cek rank dari userEngagement
        const userEngagementDoc = await getDoc(
          doc(db, 'adminDashboard', 'userEngagement', 'engagements', uid)
        )
        if (!userEngagementDoc.exists()) return null
        
        const engagement = userEngagementDoc.data() as UserEngagement
        
        // Estimate rank (rough approximation)
        return {
          rank: 100 + Math.floor((100 - engagement.engagementScore) * 10),
          isTop100: false,
          score: engagement.engagementScore,
        }
      }
      
      return {
        rank: position + 1,
        isTop100: true,
        score: data.users[position].engagementScore,
      }
    },
    enabled: !!uid,
    staleTime: STALE_TIME,
  })
}

export interface LiveLeaderboard {
  workoutsRanking: LeaderboardEntry[]
  stepsRanking: LeaderboardEntry[]
}

// Live leaderboard aggregated directly from user health subcollections.
// Used when precomputed leaderboards/overall is empty.
export function useLiveLeaderboard() {
  return useQuery<LiveLeaderboard>({
    queryKey: ['liveLeaderboard'],
    queryFn: async () => {
      const [workoutsSnap, healthSnap] = await Promise.all([
        getDocs(collectionGroup(db, 'workoutHistory')),
        getDocs(collectionGroup(db, 'health')),
      ])

      const workoutsByUser = new Map<string, number>()
      workoutsSnap.forEach((d) => {
        const uid = d.ref.parent.parent?.id
        if (uid) workoutsByUser.set(uid, (workoutsByUser.get(uid) || 0) + 1)
      })

      const stepsByUser = new Map<string, number>()
      healthSnap.forEach((d) => {
        const uid = d.ref.parent.parent?.id
        if (uid) stepsByUser.set(uid, (stepsByUser.get(uid) || 0) + (d.data().steps || 0))
      })

      const allUids = new Set([...workoutsByUser.keys(), ...stepsByUser.keys()])
      const userDocs = await Promise.all(Array.from(allUids).map((uid) => getDoc(doc(db, 'users', uid))))
      const userNames = new Map<string, string>()
      userDocs.forEach((d) => {
        if (d.exists()) userNames.set(d.id, d.data().displayName || 'Anonymous')
      })

      const workoutsRanking: LeaderboardEntry[] = Array.from(workoutsByUser.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([uid, count], i) => ({
          rank: i + 1,
          uid,
          displayName: userNames.get(uid) || 'Anonymous',
          workouts: count,
          engagementScore: count,
        }))

      const stepsRanking: LeaderboardEntry[] = Array.from(stepsByUser.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([uid, steps], i) => ({
          rank: i + 1,
          uid,
          displayName: userNames.get(uid) || 'Anonymous',
          steps,
          engagementScore: steps,
        }))

      return { workoutsRanking, stepsRanking }
    },
    staleTime: 5 * 60 * 1000,
  })
}
